import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import { leadsAPI } from '../services/api';
import { Lead } from '../types/Lead';
import { FormSubmission } from '../types/Form';
import { getStatusColor, getStatusLabel } from '../services/formApi';
import PermissionGate from './PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { 
  ArrowLeftIcon, 
  ArrowUpIcon,
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOfficeIcon, 
  MapPinIcon, 
  CalendarIcon, 
  TagIcon, 
  DocumentTextIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  HeartIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import LeadCallsTab from './LeadCallsTab';
import LeadEmailsTab from './LeadEmailsTab';
import LeadDocumentsTab from './LeadDocumentsTab';
import LeadTasksTab from './LeadTasksTab';
import LeadNotesTab from './LeadNotesTab';
import LeadTimelineTab from './LeadTimelineTab';
import LeadAssociationsTab from './LeadAssociationsTab';

// Utility functions
const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const formatDateOnly = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-800';
    case 'high': return 'bg-orange-100 text-orange-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const renderSubmissionData = (submission: FormSubmission) => {
  const data = submission.submissionData;
  
  return (
    <div className="space-y-4">
      {Object.entries(data).map(([key, value]) => {
        if (value === null || value === undefined || value === '') return null;
        
        return (
          <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
            <span className="font-medium text-gray-700 capitalize">
              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </span>
            <span className="text-gray-900">
              {Array.isArray(value) ? value.join(', ') : String(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const renderStatusIcon = (status: string) => {
  switch (status) {
    case 'submitted':
      return <ClockIcon className="w-4 h-4" />;
    case 'under_review':
      return <ClockIcon className="w-4 h-4" />;
    case 'approved':
      return <CheckCircleIcon className="w-4 h-4" />;
    case 'rejected':
      return <XCircleIcon className="w-4 h-4" />;
    case 'attached':
      return <DocumentTextIcon className="w-4 h-4" />;
    default:
      return <ClockIcon className="w-4 h-4" />;
  }
};

const LEAD_DETAIL_TABS = ['details', 'forms', 'calls', 'emails', 'documents', 'tasks', 'notes', 'associations', 'timeline'] as const;
type LeadDetailTabId = typeof LEAD_DETAIL_TABS[number];

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccessModule } = usePermissions();
  const [lead, setLead] = useState<Lead | null>(null);
  const [attachedForms, setAttachedForms] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Persist active tab in URL so it survives refresh
  const tabParam = searchParams.get('tab');
  const activeTab: LeadDetailTabId = LEAD_DETAIL_TABS.includes(tabParam as LeadDetailTabId)
    ? (tabParam as LeadDetailTabId)
    : 'details';

  const setActiveTab = (tab: LeadDetailTabId) => {
    setSearchParams({ tab });
  };
  
  // Get the referring page from query params or sessionStorage
  const getReferrerPath = () => {
    // Check URL params first (if explicitly passed)
    const fromParam = searchParams.get('from');
    if (fromParam) return fromParam;
    
    // Check sessionStorage for last visited page
    try {
      const lastPage = sessionStorage.getItem('leadshive_last_page');
      if (lastPage && lastPage !== window.location.pathname) {
        return lastPage;
      }
    } catch (e) {
      // Ignore storage errors
    }
    
    return null;
  };

  const handleBack = () => {
    // Priority 1: Use browser history if available (most accurate)
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // Priority 2: Check for stored referrer
      const referrer = getReferrerPath();
      if (referrer && referrer.startsWith('/')) {
        navigate(referrer);
      } else {
        // Priority 3: Fallback to leads list
        navigate('/leads');
      }
    }
  };
  
  // Fast edit state
  const [editField, setEditField] = useState<'stage' | 'priority' | null>(null);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [stages, setStages] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [updating, setUpdating] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Load stages and sources for fast edit
  const loadStagesAndSources = async () => {
    try {
      const [stagesRes, sourcesRes] = await Promise.all([
        leadsAPI.getStages(),
        leadsAPI.getSources()
      ]);
      
      const stagesData = stagesRes.data?.data || stagesRes.data || [];
      const sourcesData = sourcesRes.data?.data || sourcesRes.data || [];
      
      setStages(Array.isArray(stagesData) ? stagesData.filter((stage: any) => stage.isActive !== false) : []);
      setSources(Array.isArray(sourcesData) ? sourcesData.filter((source: any) => source.isActive !== false) : []);
    } catch (error) {
      console.error('Error loading stages and sources:', error);
    }
  };

  // Handle fast edit - show dropdown
  const handleFastEdit = (field: 'stage' | 'priority') => {
    if (lead) {
      setEditField(field);
      
      if (field === 'stage') {
        setShowStageDropdown(true);
        setShowPriorityDropdown(false);
      } else if (field === 'priority') {
        setShowPriorityDropdown(true);
        setShowStageDropdown(false);
      }
    }
  };

  // Handle stage selection
  const handleStageSelect = async (stageId: number) => {
    if (!lead || !id) return;
    
    try {
      setUpdating(true);
      await leadsAPI.updateLead(id, { stageId: stageId || null });
      
      // Refresh lead data
      const leadResponse = await leadsAPI.getLeadById(id);
      setLead(leadResponse.data);
      
      setShowStageDropdown(false);
    } catch (error: any) {
      console.error('Error updating lead stage:', error);
      setError(error.message || 'Failed to update lead stage');
    } finally {
      setUpdating(false);
    }
  };

  // Handle priority selection
  const handlePrioritySelect = async (priority: 'low' | 'medium' | 'high' | 'urgent') => {
    if (!lead || !id) return;
    
    try {
      setUpdating(true);
      await leadsAPI.updateLead(id, { priority });
      
      // Refresh lead data
      const leadResponse = await leadsAPI.getLeadById(id);
      setLead(leadResponse.data);
      
      setShowPriorityDropdown(false);
    } catch (error: any) {
      console.error('Error updating lead priority:', error);
      setError(error.message || 'Failed to update lead priority');
    } finally {
      setUpdating(false);
    }
  };

  // Close dropdowns
  const closeDropdowns = () => {
    setShowStageDropdown(false);
    setShowPriorityDropdown(false);
    setEditField(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if ((showStageDropdown || showPriorityDropdown) && 
          !target.closest('.stage-dropdown-container') && 
          !target.closest('.priority-dropdown-container')) {
        closeDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStageDropdown, showPriorityDropdown]);

  // Check if current active tab is still accessible and switch to safe tab if not
  useEffect(() => {
    const checkTabAccess = () => {
      let hasAccess = true;
      
      switch (activeTab) {
        case 'details':
          hasAccess = true; // Always accessible
          break;
        case 'forms':
          hasAccess = canAccessModule('forms');
          break;
        case 'calls':
          hasAccess = canAccessModule('calls');
          break;
        case 'emails':
          hasAccess = canAccessModule('emails');
          break;
        case 'documents':
          hasAccess = canAccessModule('documents');
          break;
        case 'tasks':
          hasAccess = canAccessModule('tasks');
          break;
        case 'notes':
          hasAccess = canAccessModule('notes');
          break;
        case 'associations':
          hasAccess = canAccessModule('references');
          break;
        case 'timeline':
          hasAccess = canAccessModule('timeline');
          break;
        default:
          hasAccess = true;
      }
      
      if (!hasAccess) {
        setSearchParams({ tab: 'details' });
      }
    };

    checkTabAccess();
  }, [activeTab, canAccessModule]);

  // Handle scroll to show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Back to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };


  useEffect(() => {
    const fetchLeadData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const leadResponse = await leadsAPI.getLeadById(id);
        setLead(leadResponse.data);

        const formsResponse = await leadsAPI.getAttachedForms(id);
        setAttachedForms(formsResponse.data);
        
        // Load stages and sources for fast edit
        await loadStagesAndSources();
      } catch (err: any) {
        setError(err.message || 'Failed to fetch lead details');
      } finally {
        setLoading(false);
      }
    };
    fetchLeadData();
  }, [id]);

  if (loading) {
    return (
      <Layout title="Loading Lead..." subtitle="Fetching lead details and attached forms">
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Error" subtitle="Failed to load lead details">
        <div className="text-center py-12 text-red-600">
          <p>{error}</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline">
            Go back
          </button>
        </div>
      </Layout>
    );
  }

  if (!lead) {
    return (
      <Layout title="Lead Not Found" subtitle="The requested lead could not be found.">
        <div className="text-center py-12 text-gray-600">
          <p>No lead data available.</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline">
            Go back
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Lead: ${lead.fullName}`} subtitle="Detailed view of lead information and attached forms">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={handleBack} 
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back
          </button>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <UserIcon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-gray-900">
                        {lead.title && `${lead.title} `}{lead.fullName}
                      </h1>
                      <div className="mt-2 flex items-center flex-wrap gap-3">
                        {/* Lead ID */}
                        {lead.leadUid && (
                          <span className="text-sm font-mono font-medium text-blue-600">
                            Lead ID: {lead.leadUid}
                          </span>
                        )}
                        
                        {/* Separator after Lead ID */}
                        {lead.leadUid && (
                          <span className="text-xs text-gray-400">•</span>
                        )}
                        
                        {/* Segments */}
                        {lead.segments && lead.segments.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            {lead.segments.map((segment) => (
                              <span
                                key={segment.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                style={{ backgroundColor: segment.color }}
                                title={segment.name}
                              >
                                {segment.code}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Separator before Priority */}
                        <span className="text-xs text-gray-400">•</span>
                        
                        {/* Priority Badge with Dropdown */}
                        <div className="relative priority-dropdown-container">
                          <PermissionGate module="leads" permission="edit">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFastEdit('priority');
                              }}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)} hover:opacity-80 transition-opacity`}
                            >
                              {lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)} Priority
                              <PencilIcon className="w-3 h-3 ml-1.5" />
                            </button>
                            {showPriorityDropdown && (
                              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-40">
                                {['low', 'medium', 'high', 'urgent'].map((priority) => (
                                  <button
                                    key={priority}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePrioritySelect(priority as 'low' | 'medium' | 'high' | 'urgent');
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${getPriorityColor(priority)}`}
                                  >
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </PermissionGate>
                          <PermissionGate module="leads" permission="edit" fallback={
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)}`}>
                              {lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)} Priority
                            </span>
                          }>
                            <div></div>
                          </PermissionGate>
                        </div>
                        
                        {/* Separator */}
                        <span className="text-xs text-gray-400">•</span>
                        
                        {/* Lead Stage Badge with Dropdown */}
                        <div className="relative stage-dropdown-container">
                          <PermissionGate module="leads" permission="edit">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFastEdit('stage');
                              }}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                            >
                              <div className={`w-2 h-2 rounded-full mr-1.5 ${lead.stage_color || 'bg-gray-400'}`}></div>
                              {lead.stage_name || 'No Stage'}
                              <PencilIcon className="w-3 h-3 ml-1.5" />
                            </button>
                            {showStageDropdown && (
                              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-64 max-w-xs">
                                <div className="max-h-80 overflow-y-auto">
                                  {stages.map((stage) => (
                                    <button
                                      key={stage.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStageSelect(stage.id);
                                      }}
                                      className="w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg flex items-center transition-colors"
                                    >
                                      <div className={`w-2.5 h-2.5 rounded-full mr-3 flex-shrink-0 ${stage.color || 'bg-gray-400'}`}></div>
                                      <span className="truncate">{stage.name}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </PermissionGate>
                          <PermissionGate module="leads" permission="edit" fallback={
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <div className={`w-2 h-2 rounded-full mr-1.5 ${lead.stage_color || 'bg-gray-400'}`}></div>
                              {lead.stage_name || 'No Stage'}
                            </span>
                          }>
                            <div></div>
                          </PermissionGate>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center">
                      <EnvelopeIcon className="w-4 h-4 mr-2" />
                      {lead.email}
                    </div>
                    {lead.phone && (
                      <div className="flex items-center">
                        <PhoneIcon className="w-4 h-4 mr-2" />
                        {lead.phone}
                      </div>
                    )}
                    {lead.company && (
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                        {lead.company}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Created</div>
                  <div className="text-sm text-gray-900">{formatDate(lead.createdAt)}</div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  <PermissionGate module="leads" permission="edit">
                    <button
                      onClick={() => navigate(`/leads/edit/${id}`)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PencilIcon className="w-4 h-4 mr-1" />
                      Edit Lead
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserIcon className="w-4 h-4 mr-2" />
              Lead Details
            </button>
            <PermissionGate module="forms" permission="view">
              <button
                onClick={() => setActiveTab('forms')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'forms'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                Attached Forms ({attachedForms.length})
              </button>
            </PermissionGate>
            <PermissionGate module="calls" permission="view">
              <button
                onClick={() => setActiveTab('calls')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'calls'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PhoneIcon className="w-4 h-4 mr-2" />
                Calls
              </button>
            </PermissionGate>
            <PermissionGate module="emails" permission="view">
              <button
                onClick={() => setActiveTab('emails')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'emails'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <EnvelopeIcon className="w-4 h-4 mr-2" />
                Emails
              </button>
            </PermissionGate>
            <PermissionGate module="documents" permission="view">
              <button
                onClick={() => setActiveTab('documents')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'documents'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                Documents
              </button>
            </PermissionGate>
            <PermissionGate module="tasks" permission="view">
              <button
                onClick={() => setActiveTab('tasks')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'tasks'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Tasks
              </button>
            </PermissionGate>
            <PermissionGate module="notes" permission="view">
              <button
                onClick={() => setActiveTab('notes')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'notes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Notes
              </button>
            </PermissionGate>
            <PermissionGate module="references" permission="view">
              <button
                onClick={() => setActiveTab('associations')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'associations'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                Associations
              </button>
            </PermissionGate>
            <PermissionGate module="timeline" permission="view">
              <button
                onClick={() => setActiveTab('timeline')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'timeline'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Timeline
              </button>
            </PermissionGate>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {lead.title && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
                    <p className="text-sm text-gray-900">{lead.title}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">First Name</label>
                  <p className="text-sm text-gray-900">{lead.firstName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Name</label>
                  <p className="text-sm text-gray-900">{lead.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    {lead.email}
                  </p>
                </div>
                {lead.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      {lead.phone}
                    </p>
                  </div>
                )}
                {lead.mobileNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Mobile Number</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      {lead.mobileNumber}
                    </p>
                  </div>
                )}
                {lead.alternativeMobileNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Alternative Mobile</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      {lead.alternativeMobileNumber}
                    </p>
                  </div>
                )}
                {lead.gender && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Gender</label>
                    <p className="text-sm text-gray-900 capitalize">{lead.gender}</p>
                  </div>
                )}
                {lead.dateOfBirth && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Date of Birth</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formatDateOnly(lead.dateOfBirth)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Professional Information */}
            {(lead.company || lead.jobTitle || lead.industry || lead.website || lead.linkedinUrl) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BriefcaseIcon className="w-5 h-5 mr-2" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {lead.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Company</label>
                      <p className="text-sm text-gray-900 flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                        {lead.company}
                      </p>
                    </div>
                  )}
                  {lead.jobTitle && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Job Title</label>
                      <p className="text-sm text-gray-900">{lead.jobTitle}</p>
                    </div>
                  )}
                  {lead.industry && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Industry</label>
                      <p className="text-sm text-gray-900">{lead.industry}</p>
                    </div>
                  )}
                  {lead.companySize && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Company Size</label>
                      <p className="text-sm text-gray-900">{lead.companySize}</p>
                    </div>
                  )}
                  {lead.website && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Website</label>
                      <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                        <GlobeAltIcon className="w-4 h-4 mr-2" />
                        {lead.website}
                      </a>
                    </div>
                  )}
                  {lead.linkedinUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">LinkedIn</label>
                      <a href={lead.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                        <GlobeAltIcon className="w-4 h-4 mr-2" />
                        {lead.linkedinUrl}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address Information */}
            {(lead.streetAddress || lead.addressLine1 || lead.addressLine2 || lead.city || lead.state || lead.postalCode || lead.country) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPinIcon className="w-5 h-5 mr-2" />
                  Address Information
                </h3>
                <div className="space-y-2">
                  {lead.streetAddress && (
                    <p className="text-sm text-gray-900 flex items-center">
                      <MapPinIcon className="w-4 h-4 mr-2" />
                      {lead.streetAddress}
                    </p>
                  )}
                  {lead.addressLine1 && (
                    <p className="text-sm text-gray-900 ml-6">{lead.addressLine1}</p>
                  )}
                  {lead.addressLine2 && (
                    <p className="text-sm text-gray-900 ml-6">{lead.addressLine2}</p>
                  )}
                  {(lead.city || lead.state || lead.postalCode) && (
                    <p className="text-sm text-gray-900 ml-6">
                      {[lead.city, lead.state, lead.postalCode].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {lead.country && (
                    <p className="text-sm text-gray-900 ml-6">{lead.country}</p>
                  )}
                </div>
              </div>
            )}

            {/* Lead Management */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2" />
                Lead Management
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Customer Segment(s) - First */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Customer Segment(s)</label>
                  {lead.segments && lead.segments.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {lead.segments.map((segment) => (
                        <span
                          key={segment.id}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: segment.color }}
                        >
                          {segment.name} ({segment.code})
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No segment assigned</p>
                  )}
                </div>
                
                {/* Lead Stage - Second */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Lead Stage</label>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${lead.stage_color || 'bg-gray-400'}`}></div>
                      <span className="text-sm text-gray-900">{lead.stage_name || 'No Stage'}</span>
                    </div>
                    <PermissionGate module="leads" permission="edit">
                      <button
                        onClick={() => handleFastEdit('stage')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        title="Edit Lead Stage"
                      >
                        <PencilIcon className="w-3 h-3 mr-1" />
                        Edit
                      </button>
                    </PermissionGate>
                  </div>
                </div>
                
                {/* Source - Third */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Source</label>
                  <p className="text-sm text-gray-900">{lead.source_name || 'No Source'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Priority</label>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)}`}>
                      {lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)}
                    </span>
                    <PermissionGate module="leads" permission="edit">
                      <button
                        onClick={() => handleFastEdit('priority')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        title="Edit Priority"
                      >
                        <PencilIcon className="w-3 h-3 mr-1" />
                        Edit
                      </button>
                    </PermissionGate>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Lead Date</label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {formatDate(lead.leadDate)}
                  </p>
                </div>
                {lead.followUpDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Follow-up Date</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formatDate(lead.followUpDate)}
                    </p>
                  </div>
                )}
                {lead.closeDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Close Date</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formatDate(lead.closeDate)}
                    </p>
                  </div>
                )}
                {lead.assigned_first_name && lead.assigned_last_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Assigned To</label>
                    <p className="text-sm text-gray-900">{lead.assigned_first_name} {lead.assigned_last_name}</p>
                  </div>
                )}
                {lead.lead_owner_first_name && lead.lead_owner_last_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Lead Owner</label>
                    <p className="text-sm text-gray-900">{lead.lead_owner_first_name} {lead.lead_owner_last_name}</p>
                  </div>
                )}
                {lead.creator_first_name && lead.creator_last_name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
                    <p className="text-sm text-gray-900">{lead.creator_first_name} {lead.creator_last_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Reference Information */}
            {(lead.referenceId || lead.referenceCommission) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  Reference Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {lead.referenceId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Reference ID</label>
                      <p className="text-sm text-gray-900">{lead.referenceId}</p>
                    </div>
                  )}
                  {lead.reference_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Reference Name</label>
                      <p className="text-sm text-gray-900">{lead.reference_name}</p>
                    </div>
                  )}
                  {lead.reference_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Reference Email</label>
                      <p className="text-sm text-gray-900 flex items-center">
                        <EnvelopeIcon className="w-4 h-4 mr-2" />
                        {lead.reference_email}
                      </p>
                    </div>
                  )}
                  {lead.reference_contact && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Reference Contact</label>
                      <p className="text-sm text-gray-900 flex items-center">
                        <PhoneIcon className="w-4 h-4 mr-2" />
                        {lead.reference_contact}
                      </p>
                    </div>
                  )}
                  {lead.referenceCommission && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Reference Commission</label>
                      <p className="text-sm text-gray-900">
                        {lead.referenceCommission.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {lead.notes && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  Notes
                </h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{lead.notes}</p>
              </div>
            )}

            {/* Tags */}
            {lead.tags && lead.tags.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <TagIcon className="w-5 h-5 mr-2" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {lead.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      <TagIcon className="w-3 h-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'forms' && (
          <div className="space-y-6">
            {attachedForms.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Attached Forms</h3>
                <p className="text-gray-600">This lead doesn't have any attached form submissions yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {attachedForms.map((formSubmission) => (
                  <div key={formSubmission.id} className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{formSubmission.formName}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                          <span>Submitted by: {formSubmission.submitterName || 'Anonymous'}</span>
                          <span>•</span>
                          <span>{formatDate(formSubmission.submittedAt)}</span>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(formSubmission.status)}`}>
                        {renderStatusIcon(formSubmission.status)}
                        <span className="ml-1">{getStatusLabel(formSubmission.status)}</span>
                      </span>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Submission Data</h5>
                      {renderSubmissionData(formSubmission)}
                    </div>
                    
                    {formSubmission.notes && (
                      <div className="mt-4 bg-blue-50 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-blue-900 mb-2">Internal Notes</h5>
                        <p className="text-sm text-blue-700">{formSubmission.notes}</p>
                      </div>
                    )}
                    
                    {formSubmission.status === 'rejected' && formSubmission.rejectionReason && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-red-900 mb-2">Rejection Reason</h5>
                        <p className="text-sm text-red-700">{formSubmission.rejectionReason}</p>
                      </div>
                    )}
                    
                    {formSubmission.reviewedAt && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-green-900 mb-2">Review Information</h5>
                        <div className="space-y-1 text-sm text-green-700">
                          <p>Reviewed: {formatDate(formSubmission.reviewedAt)}</p>
                          {formSubmission.reviewerFirstName && formSubmission.reviewerLastName && (
                            <p>By: {formSubmission.reviewerFirstName} {formSubmission.reviewerLastName}</p>
                          )}
                          {formSubmission.reviewComments && (
                            <p>Comments: {formSubmission.reviewComments}</p>
                          )}
                          {formSubmission.changesDescription && (
                            <p>Changes: {formSubmission.changesDescription}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'calls' && (
          <LeadCallsTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadEmail={lead.email}
            leadPhone={lead.phone}
            leadMobileNumber={lead.mobileNumber}
          />
        )}

        {activeTab === 'emails' && (
          <LeadEmailsTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadEmail={lead.email}
          />
        )}

        {activeTab === 'documents' && (
          <LeadDocumentsTab 
            leadId={id!}
            leadName={lead.fullName}
            leadEmail={lead.email}
          />
        )}

        {activeTab === 'tasks' && (
          <LeadTasksTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadEmail={lead.email}
            leadPhone={lead.phone}
          />
        )}

        {activeTab === 'notes' && (
          <LeadNotesTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadEmail={lead.email}
            leadPhone={lead.phone}
          />
        )}

        {activeTab === 'associations' && (
          <LeadAssociationsTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadSegmentIds={lead.segmentIds}
            onRefresh={() => {
              const fetchLeadData = async () => {
                if (!id) return;
                try {
                  const leadResponse = await leadsAPI.getLeadById(id);
                  setLead(leadResponse.data);
                } catch (error: any) {
                  console.error('Error refreshing lead data:', error);
                }
              };
              fetchLeadData();
            }}
          />
        )}

        {activeTab === 'timeline' && (
          <LeadTimelineTab 
            leadId={id!} 
            leadName={lead.fullName}
            leadEmail={lead.email}
          />
        )}

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
            title="Back to top"
          >
            <ArrowUpIcon className="w-5 h-5" />
          </button>
        )}

      </div>
    </Layout>
  );
};

export default LeadDetail;